import React, { useEffect, useRef, useState } from "react";
import { Camera, Trash2, Check, KeyRound, Loader2, UserCog } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Avatar, { ROLE_LABEL } from "../components/admin/Avatar";
import PhotoCropper, { preparePhoto } from "../components/admin/PhotoCropper";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const input = `w-full min-h-[44px] px-3.5 text-sm border border-gray-200 rounded-xl bg-white
  focus:outline-none focus:ring-2 focus:ring-amber-400`;

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-500 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
    </label>
  );
}

function Notice({ tone, children }) {
  if (!children) return null;
  const cls = tone === "error" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700";
  return <p className={`text-xs rounded-xl px-3 py-2.5 ${cls}`}>{children}</p>;
}

export default function ProfilePage() {
  const { authHeaders, me, setMe, role } = useAuth();
  const fileRef = useRef(null);

  const [fullName, setFullName] = useState("");
  const [mobile, setMobile]     = useState("");
  const [saving, setSaving]     = useState(false);
  const [profileMsg, setProfileMsg] = useState({});
  const [photoBusy, setPhotoBusy]   = useState(false);
  const [photoMsg, setPhotoMsg]     = useState({});
  // The chosen file, shown in the cropper before anything is uploaded.
  const [cropSrc, setCropSrc]       = useState(null);

  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg]   = useState({});

  // Always read fresh on open, so the form shows what is stored now — not
  // whatever the header happened to load at sign-in.
  useEffect(() => {
    fetch(`${API}/profile`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (j?.data) setMe(j.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (me) { setFullName(me.fullName || ""); setMobile(me.mobile || ""); }
  }, [me]);

  const dirty = !!me && (fullName.trim() !== (me.fullName || "") || mobile.trim() !== (me.mobile || ""));

  async function saveProfile(e) {
    e.preventDefault();
    setSaving(true); setProfileMsg({});
    try {
      const res = await fetch(`${API}/profile`, {
        method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ fullName, mobile }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      setMe(j.data);
      setProfileMsg({ ok: "Saved." });
    } catch (err) { setProfileMsg({ error: err.message || "Couldn't save." }); }
    finally { setSaving(false); }
  }

  /* Choosing a file only opens the cropper; nothing is uploaded until the
     person has positioned it and pressed Save. The 25 MB check is on the
     ORIGINAL — a modern phone photo is often over 5 MB — and what is
     actually uploaded is the cropped 512px JPEG, a small fraction of that. */
  async function choosePhoto(file) {
    if (fileRef.current) fileRef.current.value = "";   // allow picking the same file again
    if (!file) return;
    if (!file.type.startsWith("image/")) { setPhotoMsg({ error: "Choose an image file." }); return; }
    if (file.size > 25 * 1024 * 1024) { setPhotoMsg({ error: "That photo is over 25 MB — try a smaller one." }); return; }
    setPhotoMsg({});
    setPhotoBusy(true);
    try {
      // Not a blob: URL — see preparePhoto for why the live CSP refuses one.
      setCropSrc(await preparePhoto(file));
    } catch {
      setPhotoMsg({ error: "Couldn't open that photo. Try a JPG or PNG." });
    } finally { setPhotoBusy(false); }
  }

  function closeCropper() {
    setCropSrc(null);
  }

  async function uploadPhoto(file) {
    if (!file) return;
    setPhotoBusy(true); setPhotoMsg({});
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const res = await fetch(`${API}/profile/avatar`, { method: "POST", headers: authHeaders(), body: fd });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      setMe(j.data);
      setPhotoMsg({ ok: "Photo updated." });
      closeCropper();
    } catch (err) { setPhotoMsg({ error: err.message || "Upload failed." }); closeCropper(); }
    finally { setPhotoBusy(false); }
  }

  async function removePhoto() {
    setPhotoBusy(true); setPhotoMsg({});
    try {
      const res = await fetch(`${API}/profile/avatar`, { method: "DELETE", headers: authHeaders() });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      setMe(j.data);
    } catch (err) { setPhotoMsg({ error: err.message || "Couldn't remove the photo." }); }
    finally { setPhotoBusy(false); }
  }

  async function changePassword(e) {
    e.preventDefault();
    setPwMsg({});
    if (pw.next.length < 8) { setPwMsg({ error: "New password must be at least 8 characters." }); return; }
    if (pw.next !== pw.confirm) { setPwMsg({ error: "The two new passwords don't match." }); return; }
    setPwBusy(true);
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: pw.current, newPassword: pw.next }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      setPw({ current: "", next: "", confirm: "" });
      setPwMsg({ ok: "Password changed." });
    } catch (err) { setPwMsg({ error: err.message || "Couldn't change the password." }); }
    finally { setPwBusy(false); }
  }

  if (!me) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-500" /></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-4 sm:space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">My profile</h1>
        <p className="text-sm text-gray-500 mt-0.5">How you appear to the rest of the team.</p>
      </div>

      {/* Photo */}
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar name={me.fullName} src={me.avatarUrl} size={80} />
            {photoBusy && (
              <span className="absolute inset-0 rounded-full bg-white/70 flex items-center justify-center">
                <Loader2 size={20} className="animate-spin text-amber-500" />
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-gray-900 truncate">{me.fullName}</p>
            <p className="text-xs text-gray-500">{ROLE_LABEL[me.role] || me.role}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={photoBusy}
                className="inline-flex items-center gap-1.5 min-h-[44px] px-4 text-xs font-semibold rounded-xl text-white
                  bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-50">
                <Camera size={14} /> {me.avatarUrl ? "Change photo" : "Upload photo"}
              </button>
              {me.avatarUrl && (
                <button type="button" onClick={removePhoto} disabled={photoBusy}
                  className="inline-flex items-center gap-1.5 min-h-[44px] px-4 text-xs font-medium rounded-xl
                    border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
              onChange={e => choosePhoto(e.target.files?.[0])} />
          </div>
        </div>
        <p className="text-[11px] text-gray-400 mt-3">JPG, PNG or WebP. You can drag and zoom it into place before saving; location data is removed.</p>
        <div className="mt-2"><Notice tone={photoMsg.error ? "error" : "ok"}>{photoMsg.error || photoMsg.ok}</Notice></div>
      </section>

      {/* Details */}
      <form onSubmit={saveProfile} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-900">Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name">
            <input className={input} value={fullName} onChange={e => setFullName(e.target.value)} maxLength={80} required />
          </Field>
          <Field label="Mobile">
            <input className={input} type="tel" inputMode="tel" value={mobile} onChange={e => setMobile(e.target.value)}
              placeholder="+91 98765 43210" />
          </Field>
          <Field label="Email" hint="Your sign-in. Ask the owner if it needs to change.">
            <input className={`${input} bg-gray-50 text-gray-500`} value={me.email} readOnly />
          </Field>
          <Field label="Role" hint={role === "owner" ? "Owners manage roles from Team." : "Set by the owner."}>
            <input className={`${input} bg-gray-50 text-gray-500`} value={ROLE_LABEL[me.role] || me.role} readOnly />
          </Field>
        </div>
        <Notice tone={profileMsg.error ? "error" : "ok"}>{profileMsg.error || profileMsg.ok}</Notice>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {role === "owner" ? (
            <Link to="/admin/team" className="inline-flex items-center gap-1.5 min-h-[44px] text-xs font-medium text-amber-700 hover:text-amber-800">
              <UserCog size={14} /> Manage your team
            </Link>
          ) : <span />}
          <button type="submit" disabled={!dirty || saving}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-5 text-sm font-semibold rounded-xl text-white
              bg-gray-900 hover:bg-gray-800 disabled:opacity-40">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save changes
          </button>
        </div>
      </form>

      {/* Password */}
      <form onSubmit={changePassword} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><KeyRound size={15} className="text-gray-400" /> Password</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Current password">
            <input className={input} type="password" autoComplete="current-password" value={pw.current}
              onChange={e => setPw({ ...pw, current: e.target.value })} required />
          </Field>
          <Field label="New password" hint="At least 8 characters.">
            <input className={input} type="password" autoComplete="new-password" value={pw.next}
              onChange={e => setPw({ ...pw, next: e.target.value })} required />
          </Field>
          <Field label="Confirm new password">
            <input className={input} type="password" autoComplete="new-password" value={pw.confirm}
              onChange={e => setPw({ ...pw, confirm: e.target.value })} required />
          </Field>
        </div>
        <Notice tone={pwMsg.error ? "error" : "ok"}>{pwMsg.error || pwMsg.ok}</Notice>
        <div className="flex justify-end">
          <button type="submit" disabled={pwBusy || !pw.current || !pw.next}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-5 text-sm font-semibold rounded-xl
              border border-gray-200 text-gray-800 hover:bg-gray-50 disabled:opacity-40">
            {pwBusy && <Loader2 size={15} className="animate-spin" />} Change password
          </button>
        </div>
      </form>

      {cropSrc && <PhotoCropper src={cropSrc} busy={photoBusy} onCancel={closeCropper} onSave={uploadPhoto} />}

      {me.lastLoginAt && (
        <p className="text-[11px] text-gray-400 text-center">
          Last signed in {new Date(me.lastLoginAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
        </p>
      )}
    </div>
  );
}
